import React from "react";
import { Select, Spin, Avatar, Space } from "antd";
import debounce from "lodash/debounce";
import Apiauth from "../../../api/Apiauth";
// import { t } from "i18next";
import { FilterOutlined } from "@ant-design/icons";

// กำหนด URL ฐานของโลโก้ธนาคารไว้ที่นี่เพื่อความสะอาด
const IMG_BASE_URL = `https://assets.slipmate.ai/public/banks-logo/`;

/**
 * ภารกิจ: ดึงข้อมูลรายการลูกค้าจาก API โดยใช้ชื่อที่ค้นหาและประเภทการค้นหาที่ระบุ
 *
 * @param {string} searchString - ชื่อหรือคำค้นหาที่ผู้ใช้ป้อน
 * @param {string} typeSearch - ประเภทของการค้นหา (e.g., 'name', 'account_no')
 * @returns {Promise<Array<Object>>} - Array ของ options ที่ถูกจัดรูปแบบแล้ว
 */
async function fetchUserList(searchString, typeSearch) {
  if (!searchString) return Promise.resolve([]);
  let name = {
    typeSearch: typeSearch,
    search: searchString,
  };

  try {
    const res = await Apiauth.getSearchCustomer(name);
    if (res.status !== 200 || !Array.isArray(res.data?.data)) return [];

    const results = res.data.data;

    return results.map((user) => ({
      // key ควรเป็นค่าที่ไม่ซ้ำกัน
      key: user.customer_uuid,
      // label สำหรับแสดงใน Dropdown list
      title: user?.bank_code,
      label: `${user.name} - ${user?.bank_code} - ${user.account_no}`,
      // value ที่จะถูกส่งกลับเมื่อเลือกรายการ (customer_uuid)
      value: user.customer_uuid,
      // เก็บข้อมูลเพิ่มเติมที่จำเป็นสำหรับ optionRender
      data: {
        bankCode: user.bank_code,
        name: user.name,
      },
    }));
  } catch (error) {
    console.error("API Fetch Error:", error);
    return [];
  }
}

/**
 * Component หุ้ม Select ที่มี Debouncing ในการค้นหา
 */
class DebounceSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fetching: false,
      options: [],
      typeSearch: "name",
    };
    this.fetchRef = 0;
    this.loadOptions = this.createLoadOptions(
      props.fetchOptions,
      this.state.typeSearch,
    );
    // ใช้ debounceTimeout จาก props หรือ default ที่ 300ms
    this.debounceFetcher = debounce(
      this.loadOptions,
      props.debounceTimeout || 500,
    );
  }

  // สร้างฟังก์ชัน loadOptions ที่เป็นพื้นฐานสำหรับการ Debounce
  createLoadOptions = (fetchOptions) => async (searchString) => {
    const { typeSearch } = this.state; // ดึง typeSearch ปัจจุบันจาก state

    if (!searchString) {
      this.setState({ options: [], fetching: false });
      return;
    }

    this.fetchRef += 1;
    const fetchId = this.fetchRef;
    this.setState({ options: [], fetching: true });

    try {
      // 5. **การปรับปรุง:** ส่ง typeSearch ไปยัง fetchUserList (ซึ่งคือ fetchOptions)
      const newOptions = await fetchOptions(searchString, typeSearch);
      if (fetchId !== this.fetchRef) {
        return; // ป้องกันการอัพเดทเมื่อมี fetch ที่ใหม่กว่า
      }
      this.setState({ options: newOptions, fetching: false });
    } catch (error) {
      console.error("Fetch error:", error);
      if (fetchId === this.fetchRef) {
        this.setState({ fetching: false, options: [] });
      }
    }
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.fetchOptions !== this.props.fetchOptions ||
      prevProps.debounceTimeout !== this.props.debounceTimeout
    ) {
      // Recreate the debounced function if props change
      this.debounceFetcher.cancel();
      this.loadOptions = this.createLoadOptions(this.props.fetchOptions);
      this.debounceFetcher = debounce(
        this.loadOptions,
        this.props.debounceTimeout || 500, // แก้ไข: ใช้ 300 หรือค่าจาก props
      );
    }
  }

  componentWillUnmount() {
    this.debounceFetcher.cancel();
  }

  handleTypeSearchChange = (value) => {
    // อัปเดต typeSearch ใน state
    this.setState({ typeSearch: value, options: [] }, () => {
      // เมื่อเปลี่ยนประเภทการค้นหา ควรกระตุ้นการค้นหาใหม่ด้วยคำค้นหาปัจจุบัน หากมี
      // แต่เนื่องจาก Select หลักไม่เก็บคำค้นหาใน state เราจะเน้นไปที่การเตรียมพร้อมสำหรับคำค้นหาครั้งถัดไป
      // และล้าง options ก่อนเพื่อความชัดเจน
      // เราอาจจะสั่งให้ Select หลัก focus และแสดงผล options ที่ว่างเปล่า
      // (Ant Design Select จัดการตรงนี้เองเมื่อ options ว่าง)
    });
  };

  render() {
    const { fetching, options, typeSearch } = this.state;
    // แยก props ที่ไม่ต้องการส่งต่อให้ Select (เช่น debounceTimeout, fetchOptions)
    // ใช้ rest parameters เพื่อแยก props
    const { t, debounceTimeout, fetchOptions, ...restProps } = this.props;

    return (
      <Space.Compact style={{ width: "100%" }}>
        <Select
          prefix={<FilterOutlined />}
          onChange={this.handleTypeSearchChange}
          style={{ width: 150 }}
          defaultValue={typeSearch}
          options={[
            {
              label: t("customer name"),
              value: "name",
            },
            {
              label: t("Account No."),
              value: "account_no",
            },
            {
              label: "UUID",
              value: "customer_uuid",
            },
            {
              label: t("Telephone Number"),
              value: "telephoneNumber",
            },
          ]}
        />
        <Select
          labelInValue
          filterOption={false}
          showSearch
          onSearch={this.debounceFetcher}
          notFoundContent={
            fetching ? <Spin size="small" /> : t("No results found")
          }
          {...restProps}
          options={options}
          optionRender={(option) => {
            const avatarKey = option.data?.bankCode
              ? option.data.bankCode.toUpperCase()
              : null;

            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                {option.data && avatarKey && (
                  <Avatar
                    // ใช้โลโก้ธนาคารตาม bankCode
                    src={`${IMG_BASE_URL}/${avatarKey}.png`}
                    style={{ marginRight: 8 }}
                    size="small"
                  />
                )}
                {option.label}
              </div>
            );
          }}
        />
      </Space.Compact>
    );
  }
}

/**
 * Component หลักสำหรับการค้นหาลูกค้า
 */
class ComponentSearch extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // เก็บค่าที่ถูกเลือกในรูปแบบ { value: customer_uuid, label: "..." }
      selectedValue: undefined,
    };
    this.handleChange = this.handleChange.bind(this);
  }

  handleChange(newValue) {
    let selectedValue;

    // ตรวจสอบว่ามีการเลือกค่าหรือไม่ และเป็นรูปแบบ Object ของ labelInValue
    if (newValue && newValue.value) {
      // เก็บค่าที่ถูกเลือกไว้ใน State
      selectedValue = newValue;
    } else {
      selectedValue = undefined;
    }

    this.setState({ selectedValue }, () => {
      if (this.props.onCustomerSelected) {
        // ส่ง Object ที่มี value และ label กลับไปให้ component แม่
        this.props.onCustomerSelected(selectedValue);
      }
    });
  }

  render() {
    const { selectedValue } = this.state;
    const { t } = this.props;

    // สำหรับ labelInValue เมื่อมีการส่งค่า value เข้าไป ต้องส่งเป็น Object { value, label }
    // หากมี selectedValue อยู่แล้ว ให้ใช้ selectedValue เอง
    // หากไม่มี selectedValue แต่มี value ผ่าน props มา (กรณีใช้กับ Form/initialValues) ต้องสร้าง Object ขึ้นมา

    // **เนื่องจาก ComponentSearch ถูกใช้ใน Form, Ant Design Form จะจัดการค่า 'value' ให้**
    // เราจึงต้องเปลี่ยนวิธีส่งค่า `value` ใน `render` ของ ComponentSearch:
    // **ComponentSearch ไม่ควรเก็บ State ของค่าที่ถูกเลือก แต่ควรให้ Form จัดการ**

    // หากต้องการให้ Form จัดการค่าที่ถูกเลือก (ซึ่งเป็นแนวทางที่เหมาะสมกว่าใน Ant Design)
    // เราไม่จำเป็นต้องส่ง this.state.selectedValue เป็น value อีก

    return (
      <DebounceSelect
        // **สำคัญ:** ใช้ props.value (ค่าจาก Form) แทน this.state.selectedValue
        // และใช้ onChange เพื่อส่งค่ากลับไปให้ Form และ Component แม่

        // **หมายเหตุ:** เนื่องจากคุณกำลังใช้ ComponentSearch ภายใน Form.Item
        // ค่า `value` และ `onChange` จะถูกส่งมาจาก Form.Item โดยอัตโนมัติ
        // การกำหนด `value` ใน render นี้เป็นการ *overwrite* ค่าจาก Form.Item ซึ่งอาจเกิดปัญหา

        // แนวทางที่ถูกต้องคือ:
        // 1. ลบ this.state.selectedValue และ constructor/state ที่ไม่จำเป็น
        // 2. ใช้ this.props.value ในการแสดงผล (ถ้าจำเป็นต้องแสดงผล)

        // **เนื่องจากโค้ดเดิมใช้ State, ผมจะคง State ไว้และปรับปรุงการส่งค่า:**

        // ถ้าใช้ Form.Item:
        // value={this.props.value}
        // onChange={this.props.onChange} // ต้องมีการเรียกใช้ onChange ของ Form.Item ด้วย

        // ถ้าใช้ State:
        value={selectedValue} // ใช้ค่าที่เลือกจาก State
        onChange={this.handleChange} // ใช้ handleChange เพื่ออัพเดท State และเรียก onCustomerSelected
        placeholder={t("Search")}
        fetchOptions={fetchUserList}
        t={t}
        style={{ width: "100%" }}
      />
    );
  }
}

export default ComponentSearch;
